import { useCallback, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';

import { Icon } from '@src-common/view/components/Icon';

import { isEmpty, getNonEmptyFieldsCount } from '../utils';

import { FilterItem } from './FilterItem';
import { useFiltersDialog, IFiltersDialogProps } from './FiltersDialog';
import s from './MobileFilters.module.css';
import { IFiltersProps } from './types';

const MobileFilters = <FilterName extends string>({
  value,
  config,
  onChange,
  onRemoveFilters,
  filtersRef,
  children,
}: IFiltersProps<FilterName> & {
  onRemoveFilters: () => void;
  filtersRef: RefObject<HTMLDivElement> | undefined;
}) => {
  const handleApplyFilters: IFiltersDialogProps<FilterName>['onApplyFilters'] = useCallback(
    filters => {
      Object.entries(filters).forEach(([key, filterValue]) => {
        onChange(undefined, { name: key as FilterName, value: filterValue });
      });
    },
    [onChange],
  );

  const { openModal } = useFiltersDialog({
    filtersConfig: config,
    onApplyFilters: handleApplyFilters,
    onRemoveFilters,
    initialValues: value,
  });

  if (config.length === 0) {
    return null;
  }

  const visibleFilter = config[config.length - 1];

  const appliedFiltersCount = isEmpty(value) ? 0 : getNonEmptyFieldsCount(value);

  return (
    <>
      <div className={s.filters} ref={filtersRef}>
        <div className={s.item}>
          <div className={s.itemMain}>
            <FilterItem<FilterName>
              value={value}
              onChange={onChange}
              title={visibleFilter.title}
              name={visibleFilter.name}
              placeholder={visibleFilter.placeholder}
              options={visibleFilter.options}
              type={visibleFilter.type}
              icon={visibleFilter.icon}
              hasOneLineLabel={visibleFilter.hasOneLineLabel}
              isCompact={true}
            />
          </div>
          {config.length > 1 && (
            <button className={s.filterButton} onClick={openModal}>
              <Icon icon="filter" size="24px" />
            </button>
          )}
        </div>
      </div>
      {appliedFiltersCount > 0 && (
        <div className={s.filtersCount}>
          <FormattedMessage
            defaultMessage="Filters applied: {count}"
            id="ZPlKXC"
            description="Applied filters count label"
            values={{ count: appliedFiltersCount }}
          />
        </div>
      )}
      {children && (
        <div className={s.child} key="#children">
          {children}
        </div>
      )}
    </>
  );
};

export { MobileFilters };
